import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Stack } from '@mui/material';
import { without } from 'lodash';
import { ReactNode, Ref, forwardRef, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { SectionHeader } from '@/components/ui/SectionHeader';
import { Spacer } from '@/components/util';
import { minorTextGrey } from '@/theme';

const COLLAPSED_SECTION_STORAGE_KEY_BASE = 'sectionsCollapsed';

export type CollapsibleSectionProps = {
    isFetching?: boolean;
    isPending?: boolean;
    title: string;
    sectionId?: string;
    familyId?: string;
    icon?: ReactNode;
    children?: ReactNode;
};

export const CollapsibleSection = forwardRef(function CollapsibleSection(
    {
        isPending = false,
        isFetching = isPending,
        title,
        sectionId = title.replaceAll(/[^\w]/g, '-'),
        familyId = 'chart',
        icon,
        children,
    }: CollapsibleSectionProps,
    ref: Ref<HTMLDivElement>
) {
    const mixpanel = useMixpanel();
    const storageKey = `${COLLAPSED_SECTION_STORAGE_KEY_BASE}-${familyId}`;
    const getSessionCollapsedSections = () => sessionStorage.getItem(storageKey)?.split(',') ?? [];
    const [open, setOpen] = useState(() => !getSessionCollapsedSections().includes(sectionId));

    const handleHeaderClick = () => {
        setOpen(!open);
        mixpanel?.track('Toggle chart section', { sectionId, isOpen: !open });
        const sessionCollapsedSections = getSessionCollapsedSections();
        if (open) {
            sessionStorage.setItem(storageKey, [...sessionCollapsedSections, sectionId].join(','));
        } else {
            sessionStorage.setItem(storageKey, without(sessionCollapsedSections, sectionId)!.join(','));
        }
    };

    return (
        <Stack ref={ref} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <SectionHeader
                isLoading={isFetching}
                title={title}
                icon={icon}
                sx={{
                    cursor: 'pointer',
                    my: 1,
                    '&:hover .collapseButton': { backgroundColor: 'var(--IconButton-hoverBg)' },
                }}
                onClick={handleHeaderClick}
            >
                <Spacer />
                <IconButton className="collapseButton" sx={{ color: minorTextGrey }}>
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </SectionHeader>
            <Collapse in={!isPending && open} unmountOnExit>
                <Box sx={{ mb: 3 }}>{children}</Box>
            </Collapse>
        </Stack>
    );
});
